import { useState, useEffect } from "react";
import {
  ClipboardDocumentIcon,
  ArrowTopRightOnSquareIcon,
  ArrowPathIcon,
  CheckIcon,
  LinkIcon,
} from "@heroicons/react/24/outline";

const AFFILIATE_TAG = "theessenti044-20";

const AMAZON_DOMAINS = [
  "amazon.com", "amazon.co.uk", "amazon.ca", "amazon.de", "amazon.fr",
  "amazon.it", "amazon.es", "amazon.co.jp", "amazon.com.au", "amazon.com.br",
  "amazon.in", "amazon.com.mx", "amazon.nl", "amazon.sg", "amzn.to"
];

function extractASIN(pathname) {
  const patterns = [
    /\/dp\/([A-Z0-9]{10})/i,
    /\/gp\/product\/([A-Z0-9]{10})/i,
    /\/gp\/aw\/d\/([A-Z0-9]{10})/i,
    /\/exec\/obidos\/ASIN\/([A-Z0-9]{10})/i,
    /\/product\/([A-Z0-9]{10})/i,
  ];

  for (const pattern of patterns) {
    const match = pathname.match(pattern);
    if (match) return match[1].toUpperCase();
  }
  return null;
}

function generateAffiliateLink(inputUrl) {
  let url;
  try {
    url = new URL(inputUrl);
  } catch {
    throw new Error("Please enter a valid URL");
  }

  const hostname = url.hostname.toLowerCase().replace("www.", "");
  const isAmazon = AMAZON_DOMAINS.some(
    (domain) => hostname === domain || hostname.endsWith("." + domain)
  );

  if (!isAmazon) {
    throw new Error("This doesn't look like an Amazon URL");
  }

  // Handle short links
  if (hostname === "amzn.to") {
    url.searchParams.delete("tag");
    url.searchParams.set("tag", AFFILIATE_TAG);
    return url.toString();
  }

  const asin = extractASIN(url.pathname);
  if (!asin) {
    throw new Error("Couldn't find product ID in this URL");
  }

  return `https://${url.hostname}/dp/${asin}?tag=${AFFILIATE_TAG}`;
}

export default function LinkConverter({ initialUrl = "" }) {
  const [inputUrl, setInputUrl] = useState(initialUrl);
  const [affiliateLink, setAffiliateLink] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (initialUrl) {
      setInputUrl(initialUrl);
      handleConvert(initialUrl);
    }
  }, [initialUrl]);

  const handleConvert = (url = inputUrl) => {
    setError("");
    setAffiliateLink("");
    setCopied(false);

    if (!url.trim()) {
      setError("Paste an Amazon product URL to get started");
      return;
    }

    try {
      const link = generateAffiliateLink(url.trim());
      setAffiliateLink(link);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReset = () => {
    setInputUrl("");
    setAffiliateLink("");
    setError("");
    setCopied(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(affiliateLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const input = document.createElement("input");
      input.value = affiliateLink;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleConvert();
  };

  return (
    <section id="converter" className="py-16 sm:py-20 bg-white dark:bg-amazon-dark-light">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-amazon-orange/10 rounded-2xl mb-4">
            <LinkIcon className="h-7 w-7 text-amazon-orange" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
            Affiliate Link Generator
          </h2>
          <p className="text-gray-600 dark:text-gray-300 max-w-xl mx-auto">
            Paste any Amazon product URL and instantly get your affiliate link.
            Works with all Amazon domains worldwide.
          </p>
        </div>

        <div className="card card-dark p-6 sm:p-8">
          {/* Input Section */}
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="url"
                value={inputUrl}
                onChange={(e) => setInputUrl(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Paste Amazon product URL here..."
                className="flex-1 px-4 py-3 rounded-lg border-2 border-gray-200 dark:border-amazon-dark-lighter
                         dark:bg-amazon-dark dark:text-white
                         focus:border-amazon-orange focus:ring-0 transition-colors
                         placeholder-gray-400"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => handleConvert()}
                  className="btn-primary flex-1 sm:flex-none whitespace-nowrap"
                >
                  Convert
                </button>
                <button
                  onClick={handleReset}
                  className="p-3 rounded-lg border-2 border-gray-200 dark:border-amazon-dark-lighter
                           text-gray-500 hover:text-amazon-orange hover:border-amazon-orange
                           transition-colors"
                  title="Reset"
                >
                  <ArrowPathIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Error State */}
            {error && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* Success State */}
            {affiliateLink && (
              <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-amazon-dark-lighter">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Your Affiliate Link
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={affiliateLink}
                      readOnly
                      className="flex-1 px-4 py-3 rounded-lg bg-gray-50 dark:bg-amazon-dark
                               border-2 border-gray-200 dark:border-amazon-dark-lighter
                               text-gray-700 dark:text-gray-200 text-sm"
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleCopy}
                    className={`btn-secondary flex-1 inline-flex items-center justify-center gap-2
                              ${copied ? "!bg-green-600" : ""}`}
                  >
                    {copied ? (
                      <>
                        <CheckIcon className="h-5 w-5" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <ClipboardDocumentIcon className="h-5 w-5" />
                        Copy Link
                      </>
                    )}
                  </button>
                  <a
                    href={affiliateLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary flex-1 inline-flex items-center justify-center gap-2"
                  >
                    <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                    Open on Amazon
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-6">
          Store ID: <code className="bg-gray-100 dark:bg-amazon-dark px-2 py-1 rounded">{AFFILIATE_TAG}</code>
        </p>
      </div>
    </section>
  );
}
